import Decimal from 'decimal.js';
import { uniqueId } from 'lodash';

import {
  DispositiveProvisionDispositionKind,
  TransferMethod,
} from '@/types/schema';

import { defaultRecipient } from '../DispositiveProvisionsForm.constants';
import { DispositiveProvisionsFormRecipient } from '../DispositiveProvisionsForm.types';
import { SplitDispositionFormShape } from './SplitDispositionModal.types';

/**
 * Truncate a Decimal to at most 5 decimal places (minimum 2).
 * Uses ROUND_DOWN so the remainder is always positive and can be
 * added to the last recipient.
 */
function roundSplitValue(value: Decimal): Decimal {
  if (value.decimalPlaces() <= 2) {
    return value.toDecimalPlaces(2, Decimal.ROUND_DOWN);
  }
  return value.toDecimalPlaces(5, Decimal.ROUND_DOWN);
}

/**
 * Calculate the split percentage for each recipient.
 * Uses Decimal.js for precision with financial calculations.
 * Result is rounded to avoid repeating decimals that cause form validation issues.
 */
export function calculateSplitPercentage(
  total: Decimal,
  count: number
): Decimal {
  if (count <= 0) {
    return new Decimal(0);
  }
  return roundSplitValue(total.dividedBy(count));
}

/**
 * Calculate the split amount for each recipient.
 * Result is rounded to avoid repeating decimals that cause form validation issues.
 */
export function calculateSplitAmount(total: Decimal, count: number): Decimal {
  if (count <= 0) {
    return new Decimal(0);
  }
  return roundSplitValue(total.dividedBy(count));
}

const UNICODE_FRACTIONS: Record<number, string> = {
  2: '\u00BD', // ½
  3: '\u2153', // ⅓
  4: '\u00BC', // ¼
  5: '\u2155', // ⅕
  6: '\u2159', // ⅙
  7: '\u2150', // ⅐
  8: '\u215B', // ⅛
  9: '\u2151', // ⅑
  10: '\u2152', // ⅒
};

/**
 * Format a split fraction for display (e.g., "⅓ split" or "33.33%").
 * Shows Unicode fractions for 2–10 recipients, otherwise shows percentage.
 */
export function formatSplitFraction(
  count: number,
  percentage: Decimal
): string {
  const fraction = UNICODE_FRACTIONS[count];
  if (fraction) {
    return `${fraction} split`;
  }

  // For larger counts, show percentage
  return `${percentage.toFixed(2)}%`;
}

/**
 * Calculate the remainder-adjusted value for the last recipient so the
 * total of all split values sums exactly to the original total.
 * e.g. 100 split 3 ways → 33.33333 + 33.33333 + 33.33334 = 100
 */
function calculateLastSplitValue(
  total: Decimal,
  splitValue: Decimal,
  count: number
): Decimal {
  const sumOfOthers = splitValue.times(count - 1);
  return total.minus(sumOfOthers);
}

/**
 * Create split recipient form values from the modal form values.
 */
export function createSplitRecipients(
  formValues: SplitDispositionFormShape
): DispositiveProvisionsFormRecipient[] {
  const { distributionKind, selectedRecipients } = formValues;
  const recipientCount = selectedRecipients.length;

  if (recipientCount === 0) {
    return [];
  }

  // Pre-compute split values outside the map to avoid recalculating per-recipient
  const splitValues = (() => {
    switch (distributionKind) {
      case 'percentage': {
        const total = formValues.distributionValue ?? new Decimal(100);
        const split = calculateSplitPercentage(total, recipientCount);
        const last = calculateLastSplitValue(total, split, recipientCount);
        return { type: 'percentage' as const, split, last, total };
      }
      case 'amount': {
        const total = formValues.distributionValue ?? new Decimal(0);
        const split = calculateSplitAmount(total, recipientCount);
        const last = calculateLastSplitValue(total, split, recipientCount);
        return { type: 'amount' as const, split, last, total };
      }
      case 'formula': {
        const total = new Decimal(100);
        const split = calculateSplitPercentage(total, recipientCount);
        const last = calculateLastSplitValue(total, split, recipientCount);
        return { type: 'formula' as const, split, last, total };
      }
      case 'asset': {
        const total = formValues.dispositionAssetPercentage ?? new Decimal(100);
        const split = calculateSplitPercentage(total, recipientCount);
        const last = calculateLastSplitValue(total, split, recipientCount);
        return { type: 'asset' as const, split, last, total };
      }
      default:
        return { type: 'default' as const };
    }
  })();

  return selectedRecipients.map((recipient, index) => {
    const isLast = index === recipientCount - 1;
    const baseRecipient: DispositiveProvisionsFormRecipient = {
      ...defaultRecipient,
      recipientId: recipient.id,
      recipientKind: recipient.kind,
      transferTaxKind: recipient.transferTaxKind,
      // Generate a unique ID for the form to track this recipient
      _dispositiveProvisionId: `split-dp-${uniqueId()}`,
    };

    switch (splitValues.type) {
      case 'percentage': {
        // Split the max distribution amount evenly among recipients,
        // truncating to 2 decimal places (cents) with remainder to the last recipient
        let splitLimit: Decimal | null = null;
        if (formValues.maxDistributionAmount) {
          const perRecipient = formValues.maxDistributionAmount
            .dividedBy(recipientCount)
            .toDecimalPlaces(2, Decimal.ROUND_DOWN);
          splitLimit = isLast
            ? calculateLastSplitValue(
                formValues.maxDistributionAmount,
                perRecipient,
                recipientCount
              )
            : perRecipient;
        }
        return {
          ...baseRecipient,
          dispositionKind_ButtonGroup:
            DispositiveProvisionDispositionKind.Percentage,
          dispositionKind_Select: '',
          dispositionPercentage: isLast ? splitValues.last : splitValues.split,
          dispositionAmount: null,
          dispositionLimit: splitLimit,
        };
      }
      case 'amount':
        return {
          ...baseRecipient,
          dispositionKind_ButtonGroup:
            DispositiveProvisionDispositionKind.Amount,
          dispositionKind_Select: '',
          dispositionPercentage: null,
          dispositionAmount: isLast ? splitValues.last : splitValues.split,
        };
      case 'formula': {
        return {
          ...baseRecipient,
          dispositionKind_ButtonGroup: 'other',
          dispositionKind_Select:
            formValues.formulaKind as DispositiveProvisionsFormRecipient['dispositionKind_Select'],
          dispositionFormulaicPercentage: isLast
            ? splitValues.last
            : splitValues.split,
          dispositionAmount: null,
        };
      }
      case 'asset':
        return {
          ...baseRecipient,
          dispositionKind_ButtonGroup: 'asset' as const,
          dispositionKind_Select: '',
          dispositionPercentage: null,
          dispositionAmount: null,
          dispositionAssetPercentage: isLast
            ? splitValues.last
            : splitValues.split,
          assetGroupIds: formValues.assetGroupIds,
          assetStaticIds: formValues.assetStaticIds,
          insurancePolicyIds: formValues.insurancePolicyIds,
          ownershipStakeIds: formValues.ownershipStakeIds,
          transferMethod: formValues.transferMethod ?? TransferMethod.InKind,
        };
      default:
        return baseRecipient;
    }
  });
}
